import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { type EventType, useVenuesGetVenue } from '@cocoonspace/api'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { useLingui } from '@lingui/react/macro'
import {
	CalendarIcon,
	ClockIcon,
	Globe2Icon,
	MapPinIcon,
	VideoIcon,
} from 'lucide-react'

import { OnsiteBadge, RemoteBadge } from '../../event-types/event-type-badges'
import { useAnswerCard } from './provider'
import type { BookingRequestAnswerFormData } from '.'

const VenueSection = ({ venueId }: { venueId: string }) => {
	const { t } = useLingui()

	const { isLoading, isSuccess, data } = useVenuesGetVenue(venueId, {
		query: { select: (venue) => venue },
	})

	return (
		<div className='flex items-start gap-1'>
			<MapPinIcon className='align-baseline' />

			<div className='grid'>
				{isLoading && <span>{t`Chargement`}</span>}

				{isSuccess && (
					<div className='flex flex-col gap-1 leading-none'>
						<span className='truncate'>{data.address1}</span>
						<span className='truncate'>{`${data.postalCode || ''} ${data.city || ''}`}</span>
					</div>
				)}
			</div>
		</div>
	)
}

export const EventTypeBadge = ({
	eventType,
	className,
}: {
	className?: string
	eventType: Pick<EventType, 'location'>
}) => {
	if (eventType.location?.venue_id) {
		return <OnsiteBadge className={className} />
	}

	if (eventType.location?.type === 'remote') {
		return <RemoteBadge className={className} />
	}

	return null
}

const DurationSection = ({ eventType }: { eventType: EventType }) => {
	const duration = useMemo(() => {
		return eventType.duration < 60
			? `${eventType.duration} min`
			: dateTimeUtils
					.duration(eventType.duration, 'minutes')
					.format('H[h]mm')
					.replace('h00', 'h')
	}, [eventType.duration])

	return (
		<div className='flex items-center gap-1'>
			<ClockIcon />
			{duration}
		</div>
	)
}

const TimeSlotRecapSection = () => {
	const { t } = useLingui()

	const form = useFormContext<BookingRequestAnswerFormData>()
	const day = form.watch('day')

	const start = form.watch('start')

	const formattedDay = useMemo(() => {
		return dateTimeUtils(day).customFormat('date')
	}, [day])

	if (!day || !start) return null

	return (
		<div className='flex items-center gap-1'>
			<CalendarIcon />
			{t`Le ${formattedDay}, à ${start}`}
		</div>
	)
}

export const EventRecap = () => {
	const { eventType } = useAnswerCard()
	const { t } = useLingui()

	return (
		<div className='flex min-w-0 flex-col gap-6 px-6 pt-4 pb-5 md:justify-between md:p-8'>
			<div className='flex flex-col gap-4'>
				<div className='flex flex-col gap-2'>
					<EventTypeBadge eventType={eventType} />

					<h1 className='text-xl font-bold md:text-2xl'>{eventType.title}</h1>
				</div>

				{eventType.description && (
					<p className='leading-relaxed'>{eventType.description}</p>
				)}
			</div>

			<div className='flex flex-col gap-3 text-sm'>
				<TimeSlotRecapSection />
				<DurationSection eventType={eventType} />

				{eventType.location?.type === 'remote' && (
					<div className='flex items-center gap-2'>
						<VideoIcon className='size-4' />
						{t`Visio · lien envoyé par email`}
					</div>
				)}

				<div className='flex items-center gap-2'>
					<Globe2Icon className='size-4' />
					{eventType.timezone}
				</div>

				{eventType.location?.venue_id && (
					<VenueSection venueId={eventType.location.venue_id} />
				)}
			</div>
		</div>
	)
}
